import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import Switch from './ui/switch';
import { dashboardApi, EspConfig, SegmentCompositionEsp } from '../services/dashboardApi';
import SimplifiedModePanel from './SimplifiedModePanel';


function DeltaBadge({ value }: { value: number }) {
    if (value === 0) return <span className="text-[10px] text-gray-400">—</span>;
    const isPositive = value > 0;
    return (
        <span className={`text-[10px] font-semibold ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
            {isPositive ? '▲' : '▼'} {isPositive ? '+' : ''}{value.toLocaleString()}
        </span>
    );
}

function TierCell({ value, delta }: { value: number; delta: number }) {
    return (
        <td className="px-3 py-2.5 text-right">
            <div className="text-sm font-semibold text-gray-800">{value.toLocaleString()}</div>
            <DeltaBadge value={delta} />
        </td>
    );
}

export default function ActiveEsps() {
    const [esps, setEsps] = useState<EspConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [managingKeyFor, setManagingKeyFor] = useState<string | null>(null);
    const [apiKeyInput, setApiKeyInput] = useState('');
    const [composition, setComposition] = useState<SegmentCompositionEsp[]>([]);
    const [compositionDate, setCompositionDate] = useState<string | null>(null);
    const [compositionLoading, setCompositionLoading] = useState(true);

    // Config editing state
    const [editingConfigFor, setEditingConfigFor] = useState<string | null>(null);
    const [configInput, setConfigInput] = useState<{ list_id: string, subid1: string }>({ list_id: '', subid1: '' });

    // Growth rate editing state
    const [editingGrowthFor, setEditingGrowthFor] = useState<string | null>(null);
    const [growthInput, setGrowthInput] = useState(0);
    const [savingGrowth, setSavingGrowth] = useState(false);

    const handleSaveGrowthRate = async (espId: string) => {
        setSavingGrowth(true);
        try {
            await dashboardApi.updateEspGrowthRate(espId, growthInput);
            // Update local state so UI reflects change immediately
            setComposition(prev => prev.map(e =>
                e.esp_id === espId ? { ...e, growth_rate: growthInput } : e
            ));
            setEditingGrowthFor(null);
        } catch (err) {
            console.error('Failed to save growth rate:', err);
            alert('Failed to save growth rate');
        } finally {
            setSavingGrowth(false);
        }
    };

    const fetchEsps = async () => {
        setLoading(true);
        try {
            const data = await dashboardApi.getEsps();
            if (data && data.esps) {
                setEsps(data.esps);
            }
        } catch (error) {
            console.error("Failed to load ESPs", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchComposition = async () => {
        setCompositionLoading(true);
        try {
            const data = await dashboardApi.getSegmentComposition();
            setComposition(data.esps || []);
            setCompositionDate(data.snapshot_date);
        } catch (error) {
            console.error("Failed to load composition", error);
        } finally {
            setCompositionLoading(false);
        }
    };

    useEffect(() => {
        fetchEsps();
        fetchComposition();
    }, []);

    const toggleEsp = async (id: string, currentState: boolean) => {
        // Optimistic update
        const newState = !currentState;
        setEsps(prev => prev.map(esp =>
            esp.id === id ? { ...esp, is_active: newState } : esp
        ));

        try {
            await dashboardApi.toggleEsp(id, newState);
            console.log(`Toggled ESP ${id} to ${newState}`);
        } catch (error) {
            console.error("Failed to update ESP status", error);
            // Revert on failure
            setEsps(prev => prev.map(esp =>
                esp.id === id ? { ...esp, is_active: currentState } : esp
            ));
        }
    };

    const toggleSimplifiedMode = async (id: string, currentState: boolean) => {
        const newState = !currentState;
        setEsps(prev => prev.map(esp =>
            esp.id === id ? { ...esp, simplifiedMode: newState } : esp
        ));

        try {
            await dashboardApi.updateSimplifiedConfig(id, { simplifiedMode: newState });
        } catch (error) {
            console.error("Failed to update Simplified Mode", error);
            setEsps(prev => prev.map(esp =>
                esp.id === id ? { ...esp, simplifiedMode: currentState } : esp
            ));
        }
    };

    const handleSaveKey = async (espId: string) => {
        try {
            await dashboardApi.setEspCredentials(espId, apiKeyInput, 'set');
            setEsps(prev => prev.map(e => e.id === espId ? { ...e, has_api_key: true } : e));
            setManagingKeyFor(null);
            setApiKeyInput('');
        } catch (err) {
            console.error("Failed to save API key", err);
            alert("Failed to save API key.");
        }
    };

    const handleSaveConfig = async (espId: string) => {
        try {
            await dashboardApi.updateEspConfig(espId, configInput.list_id, configInput.subid1);
            setEsps(prev => prev.map(e => e.id === espId ? {
                ...e,
                list_id: configInput.list_id,
                subid1: configInput.subid1
            } : e));
            setEditingConfigFor(null);
        } catch (err) {
            console.error("Failed to save ESP config", err);
            alert("Failed to save ESP configuration.");
        }
    };

    const handleDeleteKey = async (espId: string) => {
        if (!window.confirm("Are you sure you want to delete this API Key?")) return;
        try {
            await dashboardApi.setEspCredentials(espId, '', 'delete');
            setEsps(prev => prev.map(e => e.id === espId ? { ...e, has_api_key: false } : e));
        } catch (err) {
            console.error("Failed to delete API key", err);
            alert("Failed to delete API key.");
        }
    };

    // Calculate global stats
    const globalStats = React.useMemo(() => {
        if (esps.length === 0) return null;
        const totalFresh = esps.reduce((acc, esp) => acc + (esp.stats?.fresh_recruits || 0), 0);

        // Aggregate global stats uniquely per geo
        const uniqueGeos = new Set();
        let totalActiveSum = 0;
        let uploadedTodaySum = 0;

        esps.forEach(esp => {
            if (!uniqueGeos.has(esp.geo) && esp.global_stats) {
                uniqueGeos.add(esp.geo);
                totalActiveSum += (esp.global_stats.total_active || 0);
                uploadedTodaySum += (esp.global_stats.uploaded_today || 0);
            }
        });

        return {
            totalActive: totalActiveSum,
            uploadedToday: uploadedTodaySum,
            totalFreshRecruits: totalFresh
        };
    }, [esps]);

    return (
        <div className="space-y-6 mb-8">
            {/* SendSegment Composition */}
            <Card className="shadow-sm border-gray-200">
                <CardHeader className="pb-2 border-b border-gray-100">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-800">SendSegment Composition</h2>
                            <p className="text-xs text-gray-500">
                                Daily tier breakdown per ESP.
                                {compositionDate && <span className="ml-1 text-gray-400">Snapshot: {compositionDate}</span>}
                            </p>
                        </div>
                        <button
                            onClick={fetchComposition}
                            className="text-xs text-blue-600 hover:text-blue-800"
                            disabled={compositionLoading}
                        >
                            Refresh
                        </button>
                    </div>
                </CardHeader>
                <CardContent className="pt-4 overflow-x-auto">
                    {compositionLoading && composition.length === 0 ? (
                        <div className="py-8 text-center text-gray-500 text-sm">Loading composition data...</div>
                    ) : composition.length === 0 ? (
                        <div className="py-8 text-center text-gray-400 text-sm">No snapshot data yet. Data appears after first allocation run.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th className="px-3 py-2 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">ESP</th>
                                    <th className="px-3 py-2 text-right text-[11px] font-bold text-indigo-600 uppercase tracking-wider">SendSegment</th>
                                    <th className="px-3 py-2 text-right text-[11px] font-bold text-gray-500 uppercase tracking-wider">Total Subs</th>
                                    <th className="px-3 py-2 text-right text-[11px] font-bold text-blue-500 uppercase tracking-wider">Active</th>
                                    <th className="px-3 py-2 text-right text-[11px] font-bold text-amber-500 uppercase tracking-wider">Warm</th>
                                    <th className="px-3 py-2 text-right text-[11px] font-bold text-violet-500 uppercase tracking-wider">Onboarding</th>
                                    <th className="px-3 py-2 text-right text-[11px] font-bold text-orange-500 uppercase tracking-wider">Re-eng</th>
                                    <th className="px-3 py-2 text-right text-[11px] font-bold text-teal-500 uppercase tracking-wider">Fresh</th>
                                    <th className="px-3 py-2 text-right text-[11px] font-bold text-emerald-600 uppercase tracking-wider">New → Active</th>
                                    <th className="px-3 py-2 text-right text-[11px] font-bold text-cyan-600 uppercase tracking-wider">Growth %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {composition.map((esp) => {
                                    const convRate = esp.total_subs > 0
                                        ? ((esp.openers / esp.total_subs) * 100).toFixed(1)
                                        : '0.0';

                                    return (
                                        <tr key={esp.esp_id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                                            <td className="px-3 py-2.5">
                                                <div className="font-semibold text-gray-800 text-sm">{esp.label}</div>
                                                <div className="text-[10px] text-gray-400 font-mono">{esp.geo.toUpperCase()} · {esp.provider}</div>
                                            </td>
                                            <td className="px-3 py-2.5 text-right">
                                                <div className={`text-sm font-bold ${esp.send_segment > 0 ? 'text-indigo-700' : 'text-gray-300'}`}>
                                                    {esp.send_segment > 0 ? esp.send_segment.toLocaleString() : '—'}
                                                </div>
                                                {esp.send_segment > 0 && <DeltaBadge value={esp.delta_send_segment} />}
                                            </td>
                                            <TierCell value={esp.total_subs} delta={esp.delta_total_subs} />
                                            <TierCell value={esp.active} delta={esp.delta_active} />
                                            <TierCell value={esp.warm} delta={esp.delta_warm} />
                                            <TierCell value={esp.onboarding} delta={esp.delta_onboarding} />
                                            <TierCell value={esp.reengagement} delta={esp.delta_reengagement} />
                                            <TierCell value={esp.fresh} delta={esp.delta_fresh} />
                                            <td className="px-3 py-2.5 text-right">
                                                <div className="text-sm font-semibold text-emerald-700">{convRate}%</div>
                                                <div className="text-[10px] text-gray-400">{esp.openers.toLocaleString()} openers</div>
                                            </td>
                                            <td className="px-3 py-2.5 text-right">
                                                {editingGrowthFor === esp.esp_id ? (
                                                    <div className="flex items-center gap-1 justify-end">
                                                        <input
                                                            type="number"
                                                            value={growthInput}
                                                            onChange={(e) => setGrowthInput(parseInt(e.target.value) || 0)}
                                                            className="w-14 text-sm text-right border rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                                                            min={0}
                                                            max={100}
                                                            autoFocus
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') handleSaveGrowthRate(esp.esp_id);
                                                                if (e.key === 'Escape') setEditingGrowthFor(null);
                                                            }}
                                                        />
                                                        <span className="text-xs text-gray-400">%</span>
                                                        <button
                                                            onClick={() => handleSaveGrowthRate(esp.esp_id)}
                                                            disabled={savingGrowth}
                                                            className="text-[10px] bg-cyan-600 text-white px-1.5 py-0.5 rounded hover:bg-cyan-700 disabled:opacity-50"
                                                        >
                                                            {savingGrowth ? '...' : '✓'}
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingGrowthFor(null)}
                                                            className="text-[10px] text-gray-400 hover:text-gray-600"
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => {
                                                            setGrowthInput(esp.growth_rate ?? 0);
                                                            setEditingGrowthFor(esp.esp_id);
                                                        }}
                                                        className="group/gr inline-flex items-center gap-1 hover:bg-cyan-50 rounded px-1.5 py-0.5 transition-colors"
                                                        title="Click to edit growth rate"
                                                    >
                                                        <span className={`text-sm font-semibold ${(esp.growth_rate ?? 0) > 0 ? 'text-cyan-700' : 'text-gray-300'}`}>
                                                            {esp.growth_rate ?? 0}%
                                                        </span>
                                                        <span className="text-[9px] text-cyan-400 opacity-0 group-hover/gr:opacity-100 transition-opacity">✎</span>
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {/* Totals row */}
                                {composition.length > 1 && (
                                    <tr className="border-t-2 border-gray-200 bg-gray-50/80">
                                        <td className="px-3 py-2.5 font-bold text-gray-700 text-sm">Total</td>
                                        <td className="px-3 py-2.5 text-right text-sm font-bold text-indigo-700">
                                            {(() => {
                                                const total = composition.reduce((s, e) => s + e.send_segment, 0);
                                                return total > 0 ? total.toLocaleString() : '—';
                                            })()}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-900">
                                            {composition.reduce((s, e) => s + e.total_subs, 0).toLocaleString()}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-700">
                                            {composition.reduce((s, e) => s + e.active, 0).toLocaleString()}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-700">
                                            {composition.reduce((s, e) => s + e.warm, 0).toLocaleString()}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-700">
                                            {composition.reduce((s, e) => s + e.onboarding, 0).toLocaleString()}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-700">
                                            {composition.reduce((s, e) => s + e.reengagement, 0).toLocaleString()}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-700">
                                            {composition.reduce((s, e) => s + e.fresh, 0).toLocaleString()}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-sm font-bold text-emerald-700">
                                            {(() => {
                                                const totalSubs = composition.reduce((s, e) => s + e.total_subs, 0);
                                                const totalOpeners = composition.reduce((s, e) => s + e.openers, 0);
                                                return totalSubs > 0 ? ((totalOpeners / totalSubs) * 100).toFixed(1) + '%' : '0%';
                                            })()}
                                        </td>
                                        <td className="px-3 py-2.5"></td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            {/* Operations Monitor */}
            <Card className="shadow-sm border-gray-200">
                <CardHeader className="pb-2 border-b border-gray-100">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-800">Operations Monitor</h2>
                            <p className="text-xs text-gray-500">Real-time system status and daily upload metrics.</p>
                        </div>
                        <button
                            onClick={fetchEsps}
                            className="text-xs text-blue-600 hover:text-blue-800"
                            disabled={loading}
                        >
                            Refresh
                        </button>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    {loading && !globalStats ? (
                        <div className="py-8 text-center text-gray-500 text-sm">Loading metrics...</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex flex-col items-center justify-center text-center">
                                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">Total Active</span>
                                <span className="text-3xl font-extrabold text-blue-700">
                                    {(globalStats?.totalActive ?? 0).toLocaleString()}
                                </span>
                                <span className="text-[10px] text-blue-400 mt-1">Subscribers in Database</span>
                            </div>

                            <div className="bg-green-50 p-4 rounded-lg border border-green-100 flex flex-col items-center justify-center text-center">
                                <span className="text-xs font-bold text-green-600 uppercase tracking-wider mb-1">Uploaded Today</span>
                                <span className="text-3xl font-extrabold text-green-700">
                                    {(globalStats?.uploadedToday ?? 0).toLocaleString()}
                                </span>
                                <span className="text-[10px] text-green-400 mt-1">Contacts pushed to ESPs</span>
                            </div>

                            <div className="bg-purple-50 p-4 rounded-lg border border-purple-100 flex flex-col items-center justify-center text-center">
                                <span className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-1">Total Fresh Recruits</span>
                                <span className="text-3xl font-extrabold text-purple-700">
                                    {(globalStats?.totalFreshRecruits ?? 0).toLocaleString()}
                                </span>
                                <span className="text-[10px] text-purple-400 mt-1">Available for Allocation</span>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ESP Configuration List */}
            <Card className="shadow-sm border-gray-200">
                <CardHeader className="pb-2 border-b border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-800">ESP Configuration</h2>
                    <p className="text-xs text-gray-500">Manage active instances and monitor connection status.</p>
                </CardHeader>
                <CardContent className="pt-4">
                    {loading && esps.length === 0 ? (
                        <div className="py-8 text-center text-gray-500 text-sm">Loading configurations...</div>
                    ) : (
                        <div className="grid grid-cols-1 gap-3">
                            {esps.length === 0 ? (
                                <div className="text-center text-gray-500 py-4">No ESPs configured.</div>
                            ) : (
                                esps.map((esp) => (
                                    <div
                                        key={esp.id}
                                        className={`p-4 rounded-lg border transition-all duration-200 ${esp.is_active
                                            ? 'bg-white border-blue-200 shadow-sm'
                                            : 'bg-gray-50 border-gray-200 opacity-75'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                        {/* Left: Info */}
                                        <div className="flex items-center gap-4">
                                            <div className={`w-2.5 h-2.5 rounded-full ${esp.is_active ? 'bg-green-500 shadow-green-200 shadow-lg' : 'bg-gray-300'}`}></div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-sm text-gray-800">{esp.label}</span>
                                                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 border border-gray-200 font-mono">
                                                        {esp.geo.toUpperCase()}
                                                    </span>
                                                    {esp.simplifiedMode && (
                                                        <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: '#e0f2fe', color: '#0369a1', borderRadius: '4px', fontWeight: 'bold' }}>
                                                            SIMPLIFIED
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 mt-1">
                                                    {editingConfigFor === esp.id ? (
                                                        <div className="flex flex-col gap-1">
                                                            <div className="flex items-center gap-2 text-xs">
                                                                <span className="text-gray-500 w-12 border-b">List ID:</span>
                                                                <input
                                                                    type="text"
                                                                    value={configInput.list_id}
                                                                    onChange={(e) => setConfigInput({ ...configInput, list_id: e.target.value })}
                                                                    className="border rounded px-1 py-0.5 w-32"
                                                                />
                                                            </div>
                                                            <div className="flex items-center gap-2 text-xs">
                                                                <span className="text-gray-500 w-12 border-b">SubID1:</span>
                                                                <input
                                                                    type="text"
                                                                    value={configInput.subid1}
                                                                    onChange={(e) => setConfigInput({ ...configInput, subid1: e.target.value })}
                                                                    className="border rounded px-1 py-0.5 w-32"
                                                                />
                                                            </div>
                                                            <div className="flex items-center gap-2 mt-1">
                                                                <button onClick={() => handleSaveConfig(esp.id)} className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded hover:bg-blue-700">Save</button>
                                                                <button onClick={() => setEditingConfigFor(null)} className="text-[10px] bg-gray-200 text-gray-700 px-2 py-0.5 rounded hover:bg-gray-300">Cancel</button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-3 text-[11px] text-gray-500 group relative">
                                                            <span>Line ID: {esp.list_id || <span className="text-gray-300 italic">None</span>}</span>
                                                            {esp.subid1 && <span>| SubID1: {esp.subid1}</span>}
                                                            <button
                                                                onClick={() => {
                                                                    setConfigInput({ list_id: esp.list_id || '', subid1: esp.subid1 || '' });
                                                                    setEditingConfigFor(esp.id);
                                                                }}
                                                                className="text-blue-500 hover:text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                                                                title="Edit Configuration"
                                                            >
                                                                ✎
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500">
                                                    {esp.stats?.last_upload?.status && (
                                                        <span className={`flex items-center gap-1 font-medium ${esp.stats.last_upload.status === 'completed' || esp.stats.last_upload.status === 'success'
                                                            ? 'text-green-600'
                                                            : 'text-red-500'
                                                            }`}>
                                                            {esp.stats.last_upload.status === 'completed' || esp.stats.last_upload.status === 'success' ? '✓' : '⚠️'}
                                                            Last: {esp.stats.last_upload.status} ({esp.stats.last_upload.size})
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right: Actions */}
                                        <div className="flex flex-col items-end gap-2">
                                            <div className="flex items-center gap-4">
                                                {esp.stats?.fresh_recruits !== undefined && esp.stats.fresh_recruits > 0 && (
                                                    <div className="text-right hidden sm:block">
                                                        <span className="block text-[10px] text-gray-400 uppercase font-bold">Pipeline</span>
                                                        <span className="text-sm font-semibold text-purple-600">{esp.stats.fresh_recruits}</span>
                                                    </div>
                                                )}

                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: esp.simplifiedMode ? '#16a34a' : '#9ca3af' }}>SIMPLIFIED</span>
                                                        <Switch
                                                            checked={!!esp.simplifiedMode}
                                                            onChange={() => toggleSimplifiedMode(esp.id, !!esp.simplifiedMode)}
                                                        />
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#6b7280' }}>ACTIVE</span>
                                                        <Switch
                                                            checked={esp.is_active}
                                                            onChange={() => toggleEsp(esp.id, esp.is_active)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* API Key Management */}
                                            <div className="mt-2 text-right">
                                                {managingKeyFor === esp.id ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="password"
                                                            value={apiKeyInput}
                                                            onChange={(e) => setApiKeyInput(e.target.value)}
                                                            className="text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            placeholder="Enter API Key"
                                                        />
                                                        <button
                                                            onClick={() => handleSaveKey(esp.id)}
                                                            className="text-[10px] bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                                                        >Save</button>
                                                        <button
                                                            onClick={() => { setManagingKeyFor(null); setApiKeyInput(''); }}
                                                            className="text-[10px] bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300"
                                                        >Cancel</button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-[10px] font-medium ${esp.has_api_key ? 'text-green-600' : 'text-orange-500'}`}>
                                                            {esp.has_api_key ? '✓ API Key Set' : '⚠️ No API Key'}
                                                        </span>
                                                        <button
                                                            onClick={() => setManagingKeyFor(esp.id)}
                                                            className="text-[10px] text-blue-600 hover:text-blue-800"
                                                        >
                                                            {esp.has_api_key ? 'Update' : 'Add'}
                                                        </button>
                                                        {esp.has_api_key && (
                                                            <button
                                                                onClick={() => handleDeleteKey(esp.id)}
                                                                className="text-[10px] text-red-600 hover:text-red-800"
                                                            >
                                                                Delete
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        </div>
                                        
                                        {/* Simplified Mode Panel */}
                                        {esp.simplifiedMode && (
                                            <div style={{ width: '100%', marginTop: '16px' }}>
                                                <SimplifiedModePanel 
                                                    esp={esp} 
                                                    onConfigChange={(updatedEsp) => {
                                                        setEsps(prev => prev.map(e => e.id === esp.id ? updatedEsp : e));
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
